import { useEffect, useState } from "react";
import { signal, useSignal } from "../net/signal";
import { Overlay } from "./Overlay";

/**
 * پرسش و پیامِ خودمان، جای confirm/alert ِ مرورگر.
 *
 * پنجره‌ی بومی «192.168.1.4:8842 says» می‌نویسد، قلم و رنگِ سیستم دارد و از
 * کلِ بازی بیرون می‌زند؛ روی گوشی هم متنِ فارسی را چپ‌چین می‌کند. این یکی همان
 * پرده و همان کارتِ نیلی-طلاییِ بقیه‌ی پنجره‌های میز است: تمرکز حبس، Esc و
 * «بازگشتِ» گوشی یعنی «نه»، و دکمه‌ی خطرناک (بیرون، خروج) قرمزِ آرام.
 *
 *   ask({ title, body, ok, danger })  → Promise<boolean>
 *   askText({ title, body, ok, … })   → Promise<string | null>
 *   notify(text)                       → پیامِ گذرا پایینِ صفحه
 */
export type Ask = {
  title: string;
  body?: string;
  /** برچسبِ دکمه‌ی تأیید — همیشه کاری که می‌کند، نه «تأیید»: «بیرون بروم»، «خارج شو». */
  ok: string;
  cancel?: string;
  /** کارِ برگشت‌ناپذیر؛ دکمه قرمزِ آرام می‌شود. */
  danger?: boolean;
};

/** پرسشی که جوابش یک رشته است — مثلِ رمزِ اتاق. */
export type AskText = {
  title: string;
  body?: string;
  ok: string;
  cancel?: string;
  placeholder?: string;
  /** رمز و مانندش: نویسه‌ها پنهان. */
  secret?: boolean;
  maxLength?: number;
};

type Pending = Ask & { resolve: (yes: boolean) => void };
type PendingText = AskText & { resolve: (v: string | null) => void };

const asking = signal<Pending | null>(null);
const askingText = signal<PendingText | null>(null);
const note = signal<{ text: string; id: number } | null>(null);

/** یک پرسش در هر لحظه؛ دومی جوابِ «نه» می‌گیرد تا پشتِ اولی پنهان نشود. */
export function ask(a: Ask): Promise<boolean> {
  return new Promise((resolve) => {
    if (asking.value) { resolve(false); return; }
    asking.value = { ...a, resolve };
  });
}

/** یک پرسشِ متنی در هر لحظه؛ دومی null می‌گیرد. */
export function askText(a: AskText): Promise<string | null> {
  return new Promise((resolve) => {
    if (askingText.value) { resolve(null); return; }
    askingText.value = { ...a, resolve };
  });
}

let noteId = 0;
export function notify(text: string): void {
  note.value = { text, id: ++noteId };
}

// برای دیدنِ پنجره در آزمونِ دستی بی سرور (کنسول): window.__bazivanAsk({...})
if (typeof window !== "undefined") (window as unknown as { __bazivanAsk?: typeof ask }).__bazivanAsk = ask;

/** یک بار در App رندر می‌شود. */
/** کارتِ پرسشِ متنی. جدا از Dialogs چون حالتِ محلی (متنِ تایپ‌شده) دارد و
 *  نباید با هر توستِ گذرا از نو ساخته شود. */
function TextAsk({ a }: { a: AskText & { resolve: (v: string | null) => void } }) {
  const [v, setV] = useState("");
  const done = (value: string | null) => { askingText.value = null; a.resolve(value); };
  return (
    <Overlay base="veil" label={a.title} onClose={() => done(null)}>
      <div className="box ask">
        <b>{a.title}</b>
        {a.body && <p>{a.body}</p>}
        <input
          autoFocus
          type={a.secret ? "password" : "text"}
          value={v}
          placeholder={a.placeholder}
          maxLength={a.maxLength ?? 64}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) done(v); }}
        />
        <div className="row" style={{ gap: 8 }}>
          <button className="btn ghost" style={{ flex: 1 }} onClick={() => done(null)}>{a.cancel ?? "بی‌خیال"}</button>
          <button className="btn" style={{ flex: 1 }} disabled={!v.trim()} onClick={() => done(v)}>{a.ok}</button>
        </div>
      </div>
    </Overlay>
  );
}

export function Dialogs() {
  const a = useSignal(asking);
  const t = useSignal(askingText);
  const n = useSignal(note);

  useEffect(() => {
    if (!n) return;
    const t = setTimeout(() => { if (note.value?.id === n.id) note.value = null; }, 2600);
    return () => clearTimeout(t);
  }, [n]);

  const answer = (yes: boolean) => {
    const cur = asking.value;
    asking.value = null;
    cur?.resolve(yes);
  };

  return (
    <>
      {a && (
        <Overlay base="veil" label={a.title} onClose={() => answer(false)}>
          <div className="box ask">
            <b>{a.title}</b>
            {a.body && <p>{a.body}</p>}
            <div className="row" style={{ gap: 8 }}>
              <button className="btn ghost" style={{ flex: 1 }} onClick={() => answer(false)}>{a.cancel ?? "نه"}</button>
              <button className={"btn" + (a.danger ? " danger" : "")} style={{ flex: 1 }} onClick={() => answer(true)}>{a.ok}</button>
            </div>
          </div>
        </Overlay>
      )}
      {t && <TextAsk a={t} />}
      {n && <div className="toastmsg" role="status" aria-live="polite">{n.text}</div>}
    </>
  );
}
